// "Notify me when available" — inline email-capture form.
//
// Markup origin: src/motion/variation-ux.js inserts a `<a class=
// "sentiments-notify-link">` next to the native WC stock indicator
// whenever the currently-selected variation is out of stock. This module
// upgrades that link into a real form:
//
//   • Click the link → form appears below it (link itself hides).
//   • Submit → POSTs to wp-admin/admin-ajax.php (action:
//     sentiments_notify_subscribe). The server-side handler in
//     includes/notify-me.php stores the request as a `sentiments_enquiry`
//     CPT entry (`enquiry_type = notify_me`), fires a "Notify Me
//     Subscription" custom event into Klaviyo (which the "Back in Stock"
//     flow segments on), and best-effort wp_mail()s support@.
//   • Success → form hides, success message stays.
//
// Variation-ux.js keeps controlling the link's `hidden` attribute based
// on stock state. We mirror that on the form: a MutationObserver watches
// the link and forces the form closed whenever the link gets hidden
// (user switched back to an in-stock variation, or to a no-selection
// state). This way the form never lingers on an in-stock pill.
//
// Logged-in users get their email pre-filled from
// `window.sentimentsNotify.userEmail`. Guests can subscribe too — the
// universal big-brand pattern (Polène / Allbirds / Everlane / ALD).

const ROOT_SELECTOR    = 'form.wp-block-add-to-cart-with-options';
const NOTIFY_LINK_SEL  = '.sentiments-notify-link';
const PRODUCT_ID_INPUT = 'input[name="product_id"]';
const SIZE_INPUT       = '.wc-block-add-to-cart-with-options-variation-selector-attribute-options__pill-input';

// Read product + variation context off the WC form. Re-read on every
// submit so the user gets a fresh snapshot even if they swapped sizes
// after opening the form.
function readProductContext(form) {
	if (!form) return { productId: 0, variationId: 0, variationLabel: '' };

	const productInput   = form.querySelector(PRODUCT_ID_INPUT);
	const productId      = productInput ? parseInt(productInput.value, 10) || 0 : 0;
	const variationInput = form.querySelector('input[name="variation_id"]');
	const variationId    = variationInput ? parseInt(variationInput.value, 10) || 0 : 0;

	// Variation label — join the currently-selected attribute pill values
	// (e.g. ["MEN", "9"] → "MEN / 9"). Falls back to empty string for
	// products with no variations.
	const checked = form.querySelectorAll(`${SIZE_INPUT}:checked`);
	const parts   = [];
	checked.forEach((input) => { if (input.value) parts.push(input.value); });
	const variationLabel = parts.join(' / ');

	return { productId, variationId, variationLabel };
}

// Cache form instances per notify-link so we mount once.
const linksWithForm = new WeakSet();

function mountForm(link, wcForm) {
	if (linksWithForm.has(link)) return; // already wired
	linksWithForm.add(link);

	// Build the form wrapper as a sibling of the link so variation-ux.js
	// can keep toggling link.hidden without affecting the form's lifecycle.
	const wrap = document.createElement('div');
	wrap.className = 'sentiments-notify';
	wrap.innerHTML = `
		<form class="sentiments-notify-form" novalidate hidden aria-label="Get notified when this is back in stock">
			<input type="email"
			       name="email"
			       class="sentiments-notify-form__input"
			       placeholder="Email address"
			       autocomplete="email"
			       required>
			<button type="submit" class="sentiments-notify-form__submit">
				<span class="sentiments-notify-form__submit-label">Notify me</span>
			</button>
			<input type="text" name="website" tabindex="-1" autocomplete="off" class="sentiments-notify-form__honeypot" aria-hidden="true">
		</form>
		<p class="sentiments-notify-form__status" role="status" aria-live="polite"></p>
	`;
	link.insertAdjacentElement('afterend', wrap);

	const inlineForm = wrap.querySelector('.sentiments-notify-form');
	const emailInput = wrap.querySelector('.sentiments-notify-form__input');
	const submitBtn  = wrap.querySelector('.sentiments-notify-form__submit');
	const statusEl   = wrap.querySelector('.sentiments-notify-form__status');

	const setStatus = (state, message) => {
		statusEl.dataset.state = state;
		statusEl.textContent   = message;
	};
	const setSubmitting = (busy) => {
		if (busy) {
			submitBtn.dataset.state = 'loading';
			submitBtn.setAttribute('aria-busy', 'true');
			submitBtn.disabled = true;
		} else {
			delete submitBtn.dataset.state;
			submitBtn.removeAttribute('aria-busy');
			submitBtn.disabled = false;
		}
	};

	const openForm = () => {
		// Pre-fill from logged-in user (skipped for guests / no value).
		const cfg = window.sentimentsNotify;
		if (cfg && cfg.userEmail && !emailInput.value) {
			emailInput.value = cfg.userEmail;
		}
		inlineForm.hidden  = false;
		wrap.dataset.state = 'open';
		// CSS hides the link visually via the sibling selector
		//   .sentiments-notify-link:has(+ .sentiments-notify[data-state])
		// so we DON'T touch link.hidden here — variation-ux.js is the sole
		// owner of that attribute. Touching it from notify-me would race
		// with our own MutationObserver below and close the form mid-open.
		requestAnimationFrame(() => emailInput.focus({ preventScroll: true }));
	};

	const closeForm = () => {
		inlineForm.hidden = true;
		delete wrap.dataset.state;
		setStatus('', '');
	};

	// Click the link → open the form. Capture phase to run before any
	// other click handler the link might pick up.
	link.addEventListener('click', (e) => {
		e.preventDefault();
		openForm();
	});

	// If variation-ux.js hides the link (user picked an in-stock variation
	// OR cleared selection), close any open form too — we never want it
	// floating on an in-stock pill. Only meaningful AFTER the form has
	// been opened (the observer keys off link.hidden going TRUE while the
	// form is open).
	const linkObserver = new MutationObserver(() => {
		if (link.hidden && wrap.dataset.state === 'open') {
			closeForm();
		}
	});
	linkObserver.observe(link, { attributes: true, attributeFilter: ['hidden'] });

	inlineForm.addEventListener('submit', async (e) => {
		e.preventDefault();
		setStatus('', '');

		if (typeof inlineForm.reportValidity === 'function' && !inlineForm.reportValidity()) return;

		const cfg = window.sentimentsNotify;
		if (!cfg || !cfg.ajax) {
			setStatus('error', 'Form is offline — email support@sentimentsindia.com instead.');
			return;
		}

		const ctx = readProductContext(wcForm);
		if (!ctx.productId) {
			setStatus('error', 'Could not identify the product — please refresh and try again.');
			return;
		}

		const data = new FormData(inlineForm);
		data.append('action',          'sentiments_notify_subscribe');
		data.append('nonce',           cfg.nonce);
		data.append('product_id',      String(ctx.productId));
		data.append('variation_id',    String(ctx.variationId));
		data.append('variation_label', ctx.variationLabel);

		setSubmitting(true);

		try {
			const response = await fetch(cfg.ajax, {
				method: 'POST',
				body: data,
				credentials: 'same-origin',
			});
			let payload;
			try { payload = await response.json(); } catch { payload = {}; }

			if (response.ok && payload && payload.ok) {
				inlineForm.hidden  = true;
				wrap.dataset.state = 'success';
				setStatus('success', payload.message || "Thanks — we'll email you when it's back.");
			} else {
				setStatus('error', (payload && payload.message) || 'Something went wrong. Please try again.');
			}
		} catch (err) {
			setStatus('error', 'Could not reach our server. Please try again in a moment.');
		} finally {
			setSubmitting(false);
		}
	});
}

export function initNotifyMe() {
	const wcForm = document.querySelector(ROOT_SELECTOR);
	if (!wcForm) return; // not a PDP

	// variation-ux.js inserts the notify link asynchronously after the
	// initial render. Use a MutationObserver to catch the insertion
	// regardless of timing, then mount the form once.
	const mountIfPresent = () => {
		const link = document.querySelector(NOTIFY_LINK_SEL);
		if (link && !linksWithForm.has(link)) mountForm(link, wcForm);
	};

	mountIfPresent();
	const docObserver = new MutationObserver(mountIfPresent);
	docObserver.observe(document.body, { childList: true, subtree: true });
}
